import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Eye } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ImageUploader } from "@/components/ImageUploader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BLANK = { title: "", category: "Nutrition", author: "LiveNeat Team", image: "", excerpt: "", content: "", tags: [], seo_title: "", seo_description: "", published: true };

export default function AdminBlogs() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blogs").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = () => {
    if (!editing.title) return toast.error("Title required");
    const req = editing.id ? api.put(`/admin/blogs/${editing.id}`, editing) : api.post("/admin/blogs", editing);
    req.then(() => { toast.success("Saved"); setEditing(null); load(); });
  };
  const del = (id) => { if (!window.confirm("Delete article?")) return; api.delete(`/admin/blogs/${id}`).then(() => { toast.success("Deleted"); load(); }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Blog Articles</h1>
        <Button onClick={() => setEditing({ ...BLANK })} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-blog-btn"><Plus className="w-4 h-4 mr-1" /> New Article</Button>
      </div>
      {!list ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> : (
        <div className="space-y-3">
          {list.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl soft-shadow p-4 flex items-center gap-4" data-testid={`admin-blog-${b.id}`}>
              <img src={b.image} alt="" className="w-16 h-16 rounded-xl object-cover bg-sand shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-500 text-ink truncate">{b.title}</h3>
                <p className="text-xs text-muted-foreground">{b.category} • {b.author} • {b.views} views {!b.published && "• Draft"}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-sand rounded-lg"><Eye className="w-4 h-4 text-olive" /></a>
                <button onClick={() => setEditing({ ...BLANK, ...b })} className="p-1.5 hover:bg-sand rounded-lg" data-testid={`edit-blog-${b.id}`}><Pencil className="w-4 h-4 text-forest" /></button>
                <button onClick={() => del(b.id)} className="p-1.5 hover:bg-sand rounded-lg"><Trash2 className="w-4 h-4 text-terracotta" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{editing?.id ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label className="text-sm">Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-xl bg-white" data-testid="bf-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Category</Label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl bg-white" /></div>
                <div><Label className="text-sm">Author</Label><Input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className="rounded-xl bg-white" /></div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Featured Image</Label>
                <ImageUploader value={editing.image} onChange={(url) => setEditing((cur) => ({ ...cur, image: url }))} label="Upload featured image" />
              </div>
              <div><Label className="text-sm">Excerpt</Label><Textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="rounded-xl bg-white" rows={2} /></div>
              <div><Label className="text-sm">Content</Label><RichTextEditor value={editing.content} onChange={(html) => setEditing((cur) => ({ ...cur, content: html }))} data-testid="bf-content" /></div>
              <div className="flex items-center gap-2"><Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} /> <Label className="text-sm">Published</Label></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="save-blog-btn">Save Article</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
